import React, {useState, useMemo} from 'react';
import {Button} from '@carbon/react';
import {Arrange} from '@carbon/icons-react';
import CustomizeTableModal, {TableHeader} from './customize-table-modal';
import './customizable-client-table.scss';

interface CustomizableClientTableProps {
    tableHeaders: TableHeader[];
    children: (props: {
        visibleColumns: string[];
        filteredHeaders: TableHeader[];
        editColumnsButton: React.ReactNode;
        onSaveColumns: () => void;
        onCancelColumns: () => void;
        modalOpen: boolean;
    }) => React.ReactNode;
    defaultVisibleColumns?: string[];
    editColumnsButtonText?: string;
    editColumnsButtonProps?: any;
    onVisibleColumnsChange?: (columns: string[]) => void;
}

const CustomizableClientTable: React.FC<CustomizableClientTableProps> = ({
    tableHeaders,
    children,
    defaultVisibleColumns,
    editColumnsButtonText = 'Edit Columns',
    editColumnsButtonProps = {},
    onVisibleColumnsChange
}) => {
    // Initialize pending columns with defaultVisibleColumns or headers with defaultVisible !== false
    const [pendingColumns, setPendingColumns] = useState<string[]>(() => {
        if (defaultVisibleColumns?.length) {
            return [...defaultVisibleColumns];
        }
        return tableHeaders
            .filter(header => header.defaultVisible !== false)
            .map(header => header.key);
    });
    
    const [modifyModalOpen, setModifyModalOpen] = useState(false);

    // Filter headers based on pending columns while maintaining original order
    const filteredHeaders = useMemo(
        () => {
            return tableHeaders.filter(h => pendingColumns.includes(h.key));
        },
        [tableHeaders, pendingColumns]
    );

    const openModifyModal = () => {
        // Keep current pendingColumns when opening modal (don't reset)
        setModifyModalOpen(true);
    };

    const handleSave = () => {
        const newVisibleColumns = pendingColumns.length ? pendingColumns : tableHeaders.map(h => h.key);
        setModifyModalOpen(false);
        onVisibleColumnsChange?.(newVisibleColumns);
    };

    const handleCancel = () => {
        setModifyModalOpen(false);
    };

    const handleRequestClose = () => {
        handleCancel(); // Use cancel logic for modal close
    };

    const handleColumnsChange = (columns: string[]) => {
        setPendingColumns(columns);
    };

    const editColumnsButton = (
        <Button 
            data-testid="edit-columns-button"
            renderIcon={Arrange} 
            size="sm" 
            kind="tertiary" 
            onClick={openModifyModal}
            {...editColumnsButtonProps}
        >
            {editColumnsButtonText}
        </Button>
    );

    return (
        <>
            {children({
                visibleColumns: pendingColumns, // Use pendingColumns as visibleColumns
                filteredHeaders,
                editColumnsButton,
                onSaveColumns: handleSave,
                onCancelColumns: handleCancel,
                modalOpen: modifyModalOpen
            })}
            
            <CustomizeTableModal
                open={modifyModalOpen}
                onRequestClose={handleRequestClose}
                onSave={handleSave}
                onCancel={handleCancel}
                tableHeaders={tableHeaders}
                pendingColumns={pendingColumns}
                onColumnsChange={handleColumnsChange}
            />
        </>
    );
};

export default CustomizableClientTable;
export type {TableHeader, CustomizableClientTableProps};